/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description Constants for the API
 * @section 4.5
 */
import { useQuery } from "react-query";
import useAdminRights from "../../backend/hooks/misc/useAdminRights";
import { filterNavItemsForUser } from "../../utils/navItemRoleFilters";
import api from "./../apicaller/api-caller";
import { ApiEndpoint } from "./../apicaller/api-consts";
import * as config from "./../configuration/index";
import { TNavItem } from "./../configuration/types";
import { useAppContext } from "./../context/AppCtxProvider";
import useCurrentUser from "./../context/UserContext";
export interface Data {
  _id: string;
  title: string;
  enabled: boolean;
  description?: string;
  group?: string | null;
  link: string;
  icon?: string | JSX.Element;
  sideBarIcon?: string | JSX.Element;
  children: Data[];
  rangecolor?: string;
  customValue1?: string; //  JS_ATTR
  adminOnly?: boolean;
}
const useNavitems = (path?: string[]) => {
  const user = useCurrentUser();
  const ctx = useAppContext();
  const isAdmin = useAdminRights();
  const hasAdminAccess = Boolean(user?.profile?.tenantAdmin === true || isAdmin);
  const workflowRoleKey =
    (user?.profile?.role || "").trim() ||
    (Array.isArray((user as any)?.groups) ? (user as any).groups.join(",") : "") ||
    "none";
  // Include tenantAdmin status in cache key to ensure proper cache invalidation
  const tenantAdminStatus = user?.profile?.tenantAdmin === true ? 'admin' : 'user';
  const navItemsList = useQuery<TNavItem>(
    `navitems-${user.profileId}-${ctx.modelId}-${tenantAdminStatus}-${workflowRoleKey}`,
    async () => {
      const result = await api.asyncCallApi(ApiEndpoint.navitems, {
        method: "GET",
      });
      if (!result.ok) {
        throw new Error(
          result.error?.message || "Failed to load navigation items"
        );
      }
      return config.System.mapNavitems(result.data as TNavItem);
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - nav structure changes rarely
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: (data) => ({
        ...data,
        children: filterNavItemsForUser(data.children, {
          profile: user?.profile,
          meaGroups:
            (user?.profile as { meaGroups?: string[] } | undefined)?.meaGroups ||
            (Array.isArray((user as any)?.groups) ? (user as any).groups : null),
          hasAdminAccess,
        }),
      }),
    }
  );
  return navItemsList;
};
export default useNavitems;
